import {
  LuxButton,
  LuxIconButton,
  LuxTypography,
  LuxSelectablePill,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ColumnWrapper,
  DetailSection,
  InsPtCtDetailsWrapper,
  InsuranceColumn,
  InsuranceContainer,
  PatientPill,
  SubSectionTitle,
} from '../BookingDetails.styles';
import { TruePatient } from '@utils/interfaces';
import { useUrlsContext } from '@utils/contexts/UrlsContext';
import useGetTextColor from '@hooks/useGetTextColor';

interface InsuranceSectionProps {
  patients: Array<TruePatient>;
  currentPatient: number;
  onEditClick: (url: string, step: StepsStep) => void;
  isFullRecap?: boolean;
  hideEditButton?: boolean;
  isLast?: boolean;
  isTyp?: boolean;
}

export const InsuranceSection = ({
  patients,
  currentPatient,
  isFullRecap,
  onEditClick,
  hideEditButton = false,
  isLast = false,
  isTyp = false,
}: InsuranceSectionProps) => {
  const { t } = useTranslation();
  const { Urls } = useUrlsContext();
  const { getTextColor } = useGetTextColor();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const showOldPatientDetails = useMemo(() => patients.length > 1, [patients]);
  const insuranceType = useMemo(
    () => patients[currentPatient]?.patient?.insurance?.type,
    [currentPatient, patients],
  );

  const isPatientSelected = useCallback(
    (key: number) => currentPatient === key,
    [currentPatient],
  );

  const getPatientName = useCallback(
    (index: number) => patients[index]?.content,
    [patients],
  );

  return (
    <DetailSection isLast={isLast}>
      <SubSectionTitle>
        <LuxTypography variant="textMedium">
          {t('detailsSection.insurance')}
        </LuxTypography>
        {!hideEditButton && (
          <LuxButton
            variant="ghost"
            iconStart="EditIcon"
            size="small"
            onClick={() => onEditClick(Urls.INSURANCE, StepsStep.INSURANCE)}
          >
            {t('generalCta.edit')}
          </LuxButton>
        )}
      </SubSectionTitle>
      <InsPtCtDetailsWrapper halfWidth={isFullRecap}>
        {patients.map((patient, index) => {
          if (
            (patient.tabKey === currentPatient && patient.patient?.insurance) ||
            (patient.tabKey !== currentPatient &&
              showOldPatientDetails &&
              patient.patient?.insurance)
          ) {
            return (
              <ColumnWrapper key={index} isRow={isDesktop && isTyp}>
                {patients.length > 1 && patient.patient.insurance && (
                  <PatientPill
                    customTextColor={getTextColor(
                      !isTyp && isPatientSelected(patient.tabKey)
                        ? theme.palette.LUX.obTokens.bgColorBrandPrimary
                        : theme.palette.LUX.obTokens.bgColorBaseDefault,
                    )}
                    customColor={
                      !isTyp && isPatientSelected(patient.tabKey)
                        ? theme.palette.LUX.obTokens.bgColorBrandPrimary
                        : theme.palette.LUX.obTokens.bgColorBaseDefault
                    }
                    customBorderColor={
                      theme.palette.LUX.obTokens.bordersColorNeutralSecondary
                    }
                  >
                    <LuxIconButton
                      icon="UserIcon"
                      variant="bare"
                      size="small"
                      color={
                        isPatientSelected(patient.tabKey) && !isTyp
                          ? 'white'
                          : 'black'
                      }
                      sx={{ pointerEvents: 'none' }}
                    />
                    {getPatientName(index)}
                  </PatientPill>
                )}
                <InsuranceContainer>
                  <InsuranceColumn>
                    {patient.patient.insurance?.id &&
                      patient.patient.insurance.id !== '' && (
                        <LuxTypography
                          variant="textMdRegular"
                          color={'lightsteelblue'}
                        >
                          {/*"Other" insurance options is considered as Vision, not Medical */}
                          {[
                            StaticValues.INSURANCE_TYPE_VISION,
                            StaticValues.INSURANCE_TYPE_OTHER,
                          ].includes(insuranceType as StaticValues)
                            ? t('detailsSection.visionIns')
                            : t('detailsSection.medicalIns')}
                        </LuxTypography>
                      )}
                    {!patient.patient.insurance?.id ? (
                      <LuxTypography>-</LuxTypography>
                    ) : (
                      <LuxSelectablePill
                        selectable={false}
                        variant="pill"
                        color="info"
                        text={
                          patient.patient.insurance?.other ||
                          patient.patient.insurance?.name
                        }
                        style={{
                          maxWidth: '200px',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          display: 'block',
                        }}
                      />
                    )}
                    {patient.patient.insurance?.memberId && (
                      <LuxTypography variant="textMdRegular">
                        {t('detailsSection.memberId') +
                          patient.patient.insurance.memberId}
                      </LuxTypography>
                    )}
                    {patient.patient.insurance?.holder && (
                      <LuxTypography variant="textMdRegular">
                        {t('detailsSection.insuranceHolder') +
                          patient.patient.insurance.holder}
                      </LuxTypography>
                    )}
                  </InsuranceColumn>
                </InsuranceContainer>
              </ColumnWrapper>
            );
          } else {
            return null;
          }
        })}
      </InsPtCtDetailsWrapper>
    </DetailSection>
  );
};
